import { useCallback } from 'react';
import { useLocation } from 'react-router';
import { enableModalConfirmationAutofocus } from '@gonfalon/dogfood-flags/dogfood-flags';
import { Button, Dialog, Heading, IconButton, Modal, ModalOverlay } from '@launchpad-ui/components';

import { trackPageLeaveEvent } from './internal/analytics';

export type NavigationPromptModalProps = {
  onCancelNavigation(): void;
  onConfirmNavigation(): void;
  title?: string;
  body?: string;
  cancelText?: string;
  confirmText?: string;
};

export function NavigationPromptModal({
  onCancelNavigation,
  onConfirmNavigation,
  title = 'You have unsaved changes',
  body = 'If you leave this page, you will lose any unsaved changes',
  cancelText = 'Return to editing',
  confirmText = 'Leave page',
}: NavigationPromptModalProps) {
  const { pathname } = useLocation();
  const trackRendered = useCallback(
    (node: HTMLElement | null) => {
      if (node === null) {
        return;
      }
      trackPageLeaveEvent('Leave Page Confirmation Dialogue Rendered', { url: pathname });
    },
    [pathname],
  );

  const handleOpenChange = (isOpen: boolean) => {
    !isOpen && onCancelNavigation();
  };

  return (
    <ModalOverlay defaultOpen onOpenChange={handleOpenChange}>
      <Modal size="small">
        <Dialog>
          <div ref={trackRendered} slot="header">
            <Heading slot="title">{title}</Heading>
            <IconButton aria-label="close" icon="cancel" size="small" variant="minimal" slot="close" />
          </div>
          <div slot="body">
            <p>{body}</p>
          </div>
          <div slot="footer">
            <Button onPress={onCancelNavigation}>{cancelText}</Button>
            <Button variant="destructive" onPress={onConfirmNavigation} autoFocus={enableModalConfirmationAutofocus()}>
              {confirmText}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
